<template>
  <div class="page-container" v-wechat-title="titleTop">
    
    <!-- 1. 氛围背景光斑 (Blobs) -->
    <div v-if="data" class="blob-wrapper">
      <div class="blob blob-1" :class="data.isPass ? 'blob-pass-1' : 'blob-fail-1'"></div>
      <div class="blob blob-2" :class="data.isPass ? 'blob-pass-2' : 'blob-fail-2'"></div>
      <div class="blob blob-3" :class="data.isPass ? 'blob-pass-3' : 'blob-fail-3'"></div>
    </div>

    <!-- 2. 顶部导航 Header (已移除关闭图标) -->
    <header class="header">
      <div class="header-inner">
        <h1 class="header-title">考试结果</h1>
      </div>
    </header>

    <!-- 3. 主内容区 Main -->
    <main class="main-content" v-if="data">
      
      <!-- Result Card -->
      <div class="result-card fade-up">
        
        <!-- Badge -->
        <div class="status-badge" :class="data.isPass ? 'badge-pass' : 'badge-fail'">
          <svg v-if="data.isPass" viewBox="0 0 512 512" width="12" height="12" fill="currentColor" style="display:inline-block; margin-right:5px;">
            <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/>
          </svg>
          <svg v-else viewBox="0 0 512 512" width="12" height="12" fill="currentColor" style="display:inline-block; margin-right:5px;">
            <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/>
          </svg>
          <span>{{ data.isPass ? '测试通过' : '测试未通过' }}</span>
        </div>

        <!-- Exam Name -->
        <h2 class="exam-title">{{ data.examName || 'SSI Enriched Air Nitrox' }}</h2>

        <!-- User/Date Capsule -->
        <div class="info-capsule">
          <div class="info-item strong">
            <svg viewBox="0 0 448 512" width="10" height="10" fill="currentColor">
              <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/>
            </svg>
            <span class="truncate" style="margin-left:5px;">{{ GetUserName() }}</span>
          </div>
          <div class="capsule-divider"></div>
          <div class="info-item">
            <svg viewBox="0 0 448 512" width="10" height="10" fill="currentColor">
              <path d="M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24V64H64C28.7 64 0 92.7 0 128v16 48V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V192 144 128c0-35.3-28.7-64-64-64H344V24c0-13.3-10.7-24-24-24s-24 10.7-24 24V64H152V24zM48 192H400V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192z"/>
            </svg>
            <!-- 字体修正：使用 font-mono -->
            <span class="font-mono" style="margin-left:5px;">{{ currentDate }}</span>
          </div>
        </div>

        <!-- Score Circle -->
        <div class="score-circle-container">
          <svg style="width:0;height:0;position:absolute;" aria-hidden="true">
            <defs>
              <linearGradient id="pass-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#0057FF" />
                <stop offset="100%" stop-color="#06B6D4" />
              </linearGradient>
              <linearGradient id="fail-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#EF4444" />
                <stop offset="100%" stop-color="#F97316" />
              </linearGradient>
            </defs>
          </svg>

          <svg class="progress-ring-bg" viewBox="0 0 192 192">
            <circle cx="96" cy="96" r="80" stroke="#F3F4F6" stroke-width="10" fill="none"></circle>
          </svg>

          <svg class="progress-ring-fg" viewBox="0 0 192 192">
            <circle 
              class="progress-circle" 
              cx="96" cy="96" r="80" 
              stroke-width="10" 
              fill="none" 
              stroke-linecap="round"
              :stroke="data.isPass ? 'url(#pass-gradient)' : 'url(#fail-gradient)'"
              :style="{ strokeDashoffset: circleOffset }"
            ></circle>
          </svg>

          <div class="score-content">
            <!-- 字体修正：使用 font-mono -->
            <span class="score-num font-mono" :class="data.isPass ? 'text-gradient-pass' : 'text-gradient-fail'">
              {{ animatedScore }}
            </span>
            <span class="score-label">最终得分</span>
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid">
          <div class="stat-box">
            <div class="stat-label">总用时</div>
            <!-- 字体修正：使用 font-mono -->
            <div class="stat-value dark font-mono">{{ durationText }}</div>
          </div>
          <div class="stat-box">
            <div class="stat-label">正确率</div>
            <!-- 字体修正：使用 font-mono -->
            <div class="stat-value font-mono" :class="data.isPass ? 'text-brand-blue' : 'text-brand-red'">
              {{ finalPercentage }}%
            </div>
          </div>
        </div>

      </div>

      <!-- 4. Buttons -->
      <div class="button-container fade-up-delay">
        <button 
          class="main-btn group" 
          :class="data.isPass ? 'btn-grad-pass' : 'btn-grad-fail'"
          @click="onELearn"
        >
          <span>返回在线学习</span>
          <svg viewBox="0 0 448 512" fill="currentColor" width="12" height="12" class="arrow-icon">
             <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"/>
          </svg>
        </button>
      </div>

    </main>
  </div>
</template>

<script>
import request from '@/utils/request'
import { getUrlKey, GetBaseTitle,getUserBase  } from '@/utils/base'

export default {
  name: 'ExamResultFinal',
  data() {
    return {
      titleTop: '',
      title: '测验结果',
      backurl: '/dive',
      studentId: undefined,
      id: undefined,
      data: undefined,
      animatedScore: 0,
      circleOffset: 502,
      circumference: 502,
      userData: {
        icon: undefined
      },
    }
  },
  computed: {
    currentDate() {
      const now = new Date()
      return `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}`
    },
    studentNameDisplay() {
      if (!this.data) return 'Student'
      return this.data.studentName || this.data.nickName || this.data.userName || 'James Doe'
    },
    finalPercentage() {
      if (!this.data || !this.data.totalScore) return 0
      return parseInt(this.data.myScore * 100 / this.data.totalScore + 0.5)
    },
    durationText() {
      if (this.data && (this.data.totalSecond || this.data.totalSecond === 0)) {
        const m = Math.floor(this.data.totalSecond / 60)
        const s = this.data.totalSecond % 60
        return `${m}:${s < 10 ? '0' + s : s}`
      }
      return '45:20'
    }
  },
  created() {
    this.titleTop = GetBaseTitle()
    this.id = this.$route.params.id
    this.studentId = getUrlKey('student')
    this.init()
    getUserBase(data => {
      this.titleTop = GetBaseTitle()
      this.userData = data
    }, true)
  },
  methods: {
    GetUserName(){
      if(this.userData.localName)
      {
        return this.userData.localName;
      }
      if(this.userData.firstName)
      {
        return this.userData.firstName+" "+this.userData.lastName;
      }
      return this.userData.nickName;
    },
    init() {
      request({
        url: '/DiveLearn/getExamResult',
        method: 'post',
        data: {
          studentId: this.studentId,
          examResultId: this.id
        }
      }).then(response => {
        this.titleTop = GetBaseTitle()
        if (!response.success) { return }
        this.data = response.data
        this.$nextTick(() => {
          this.runAnimations()
        })
      })
    },
    runAnimations() {
      const targetScore = this.finalPercentage
      const duration = 1500
      let startTimestamp = null
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp
        const progress = Math.min((timestamp - startTimestamp) / duration, 1)
        const easeProgress = 1 - Math.pow(1 - progress, 4)
        this.animatedScore = Math.floor(easeProgress * targetScore)
        if (progress < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
      const offset = this.circumference - ((targetScore / 100) * this.circumference)
      setTimeout(() => {
        this.circleOffset = offset
      }, 100)
    },
    onELearn() {
      this.$router.push('/divetrip/myExam')
      // if (this.studentId && this.studentId !== 'null' && this.studentId !== 'undefined') {
      //   this.$router.push('/divetrip/eLearn/' + this.studentId)
      // } else {
      //   this.$router.push('/divetrip')
      // }
    }
  }
}
</script>

<style scoped>
/* 
  字体修正: 引入 Inter 和 JetBrains Mono 
*/
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');

* { box-sizing: border-box; }

.page-container {
  background-color: #F9FAFB; 
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow-x: hidden; 
  /* 全局字体设置为 Inter，对应 Tailwind 的 font-sans */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* 专门用于数字和代码风格的字体类，对应 Tailwind 的 font-mono */
.font-mono {
  font-family: 'JetBrains Mono', monospace;
}

/* --- Header --- */
.header {
  position: sticky;
  top: 0;
  z-index: 40;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid #F3F4F6;
  height: 3.5rem;
}

.header-inner {
  max-width: 28rem;
  margin: 0 auto;
  height: 100%;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center; /* 居中标题 */
  position: relative;
}

.header-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #6B7280;
  letter-spacing: 0.025em;
  margin: 0;
}

/* --- Blobs --- */
.blob-wrapper {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.blob {
  position: absolute;
  border-radius: 9999px;
  mix-blend-mode: multiply;
  filter: blur(64px);
  animation: blob 7s infinite;
}

.blob-pass-1 { top: 0; left: 0; width: 16rem; height: 16rem; background-color: #DBEAFE; opacity: 0.7; }
.blob-pass-2 { top: 0; right: 0; width: 16rem; height: 16rem; background-color: #CFFAFE; opacity: 0.7; animation-delay: 2s; }
.blob-pass-3 { bottom: -2rem; left: 5rem; width: 18rem; height: 18rem; background-color: #E0E7FF; opacity: 0.7; animation-delay: 4s; }

.blob-fail-1 { top: 0; left: 0; width: 16rem; height: 16rem; background-color: #FEE2E2; opacity: 0.6; }
.blob-fail-2 { top: 0; right: 0; width: 16rem; height: 16rem; background-color: #FFEDD5; opacity: 0.6; animation-delay: 2s; }
.blob-fail-3 { bottom: -2rem; left: 5rem; width: 18rem; height: 18rem; background-color: #FEF9C3; opacity: 0.6; animation-delay: 4s; }

@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}

/* --- Main Content --- */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
  position: relative;
  z-index: 10;
}

/* --- Card --- */
.result-card {
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  width: 100%;
  border-radius: 1.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(255, 255, 255, 1);
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.fade-up { animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; transform: translateY(20px); }
@keyframes fadeUp { to { opacity: 1; transform: translateY(0); } }

/* --- Badge --- */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}
.badge-pass { background-color: #F0FDF4; border: 1px solid #DCFCE7; color: #16A34A; }
.badge-fail { background-color: #FEF2F2; border: 1px solid #FEE2E2; color: #DC2626; }

/* --- Exam Title --- */
.exam-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0A2540;
  margin: 0 0 0.75rem 0;
}

/* --- Capsule --- */
.info-capsule {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 1rem;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 9999px;
  margin-bottom: 2rem;
  max-width: 100%;
}
.info-item { display: flex; align-items: center; gap: 0.375rem; color: #9CA3AF; font-size: 0.75rem; }
.info-item.strong { color: #4B5563; font-weight: 700; }
.capsule-divider { width: 1px; height: 0.75rem; background-color: #D1D5DB; }
.truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100px; }

/* --- Circle --- */
.score-circle-container {
  position: relative;
  width: 12rem;
  height: 12rem;
  margin: 0 auto 2.5rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.progress-ring-bg, .progress-ring-fg { position: absolute; inset: 0; width: 100%; height: 100%; }
.progress-ring-fg { filter: drop-shadow(0 4px 6px rgba(0,0,0,0.1)); }

.progress-circle {
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  transition: stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  stroke-dasharray: 502;
}

.score-content { z-index: 10; display: flex; flex-direction: column; align-items: center; }
.score-num {
  font-size: 4.5rem;
  font-weight: 700;
  letter-spacing: -0.05em;
  line-height: 1;
}

.text-gradient-pass {
  background: linear-gradient(135deg, #0057FF 0%, #06B6D4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.text-gradient-fail {
  background: linear-gradient(135deg, #EF4444 0%, #F97316 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.score-label {
  font-size: 0.75rem;
  color: #9CA3AF;
  font-weight: 700;
  text-transform: uppercase;
  margin-top: 0.25rem;
}

/* --- Stats Grid --- */
.stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.stat-box { background-color: #F9FAFB; border-radius: 1rem; padding: 1rem; border: 1px solid #F3F4F6; }
.stat-label { font-size: 0.75rem; color: #9CA3AF; margin-bottom: 0.25rem; }
.stat-value { font-size: 1.125rem; font-weight: 700; }
.stat-value.dark { color: #0A2540; }
.text-brand-blue { color: #0057FF; }
.text-brand-red { color: #EF4444; }

/* --- Button --- */
.button-container { width: 100%; margin-top: 2rem; }
.fade-up-delay { animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; transform: translateY(20px); animation-delay: 0.2s; }

.main-btn {
  width: 100%;
  padding: 1rem;
  border-radius: 0.75rem;
  font-weight: 700;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: transform 0.1s;
}
.main-btn:active { transform: scale(0.98); }

.btn-grad-pass {
  background: linear-gradient(to right, #0057FF, #06B6D4);
  box-shadow: 0 10px 30px -10px rgba(0, 87, 255, 0.3);
}
.btn-grad-pass:hover { background: linear-gradient(to right, #2563EB, #0891B2); }

.btn-grad-fail {
  background: linear-gradient(to right, #EF4444, #F97316);
  box-shadow: 0 10px 30px -10px rgba(239, 68, 68, 0.3);
}
.btn-grad-fail:hover { background: linear-gradient(to right, #DC2626, #EA580C); }

.arrow-icon { opacity: 0.7; transition: transform 0.3s; }
.main-btn:hover .arrow-icon { transform: translateX(4px); }
</style>